import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import ReactModalWrap from "../../components/UI/Modals/ReactModalWrap/ReactModalWrap";
import Button from "../../components/UI/Buttons/Button/Button";
import styles from "./RFZOTherapyReminderGroupModal.module.scss";
import axios, { baseUrl } from "../../axios/axios";
import TextField from "../../components/TextField/TextField";
import deleteIcon from "../../assets/icons/trash.svg";
import { useMarketplaceInfo } from "../../hooks/useMarketplaceInfo";

type Props = {
  customButton?: any;
  onGroupCreated?: () => Promise<void>;
};

export default function RFZOTherapyReminderGroupModal({
  customButton,
  onGroupCreated,
}: Props) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [groupList, setGroupList] = useState<any[]>([]);

  const [textFieldValue, setTextFieldValue] = useState<string>("");

  const location = useLocation();

  const [inputError, setInputError] = useState<boolean>(false);

  // Use marketplace info hook
  const { branchMarketplaceId, marketplaceId, isReady } = useMarketplaceInfo();

  useEffect(() => {
    if (isOpen && isReady) {
      fetchWrap(setGroupList, branchMarketplaceId, marketplaceId);
    }
  }, [isOpen, isReady, branchMarketplaceId, marketplaceId]);

  const validate = (value: string) => {
    if (value === "") {
      return true;
    }
    return groupList?.find((group: any) => {
      if (group.name == value) {
        return true;
      } else {
        return false;
      }
    });
  };

  const onChangeWrap = (value: string) => {
    setTextFieldValue(value);
    if (validate(value)) {
      setInputError(true);
    } else {
      setInputError(false);
    }
  };

  const getLinkForGroup = (groupId: string) => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set("groups", groupId);

    return {
      pathname: location.pathname,
      search: newSearchParams.toString(),
    };
  };

  return (
    <div className={styles.modalContainer}>
      {!customButton && (
        <Button
          className={styles.modalButton}
          onClick={() => setIsOpen(true)}
          buttonType="blue"
        >
          Tvoje liste
        </Button>
      )}
      {customButton && (
        <Button
          className={customButton?.class}
          onClick={() => setIsOpen(true)}
          buttonType={customButton?.buttonType}
        >
          {customButton?.text}
        </Button>
      )}
      {isOpen && (
        <ReactModalWrap
          customStyle={{ content: { width: "400px" } }}
          isOpen={isOpen}
          close={() => setIsOpen(false)}
        >
          <div className={styles.contentWrap}>
            <div className={styles.header}>Tvoje liste</div>
            <div className={styles.listWrap}>
              {groupList?.map((group: any, index: number) => {
                return (
                  <div key={group.id} className={styles.listItem}>
                    <Link
                      to={getLinkForGroup(group.id.toString())}
                      className={styles.listItemName}
                      aria-label={`Prikaži podsetnike za listu: ${group.name}`}
                      title="Kliknite da prikažete podsetnike za ovu listu"
                      onClick={() => setIsOpen(false)}
                    >
                      {group.name}
                    </Link>
                    <button
                      onClick={() => {
                        deleteGroup(group.id).then(() => {
                          fetchWrap(
                            setGroupList,
                            branchMarketplaceId,
                            marketplaceId
                          );
                        });
                      }}
                    >
                      <img src={deleteIcon} alt="Obriši" />
                    </button>
                  </div>
                );
              })}
            </div>
            <hr />
            <TextField
              value={textFieldValue}
              onChange={(val: any) => onChangeWrap(val)}
              wrapClass={styles.textField}
              validator={validate}
              label="Dodaj novu listu"
              placeholder="Unesite naziv liste"
            />
            {inputError && (
              <div className={styles.inputError}>
                Lista sa ovim nazivom već postoji.
              </div>
            )}
            <Button
              disabled={inputError || textFieldValue == "" || !isReady}
              buttonType="blue"
              onClick={() => {
                // Guard against missing marketplace info and pass IDs
                if (!isReady) {
                  console.warn(
                    "Cannot create group: marketplace info not ready"
                  );
                  return;
                }
                createGroup(
                  textFieldValue,
                  branchMarketplaceId,
                  marketplaceId
                ).then(() => {
                  fetchWrap(setGroupList, branchMarketplaceId, marketplaceId);
                  setTextFieldValue("");
                  if (onGroupCreated) {
                    onGroupCreated();
                  }
                });
              }}
            >
              Sačuvaj
            </Button>
          </div>
        </ReactModalWrap>
      )}
    </div>
  );
}

// Updated function to accept marketplace IDs as parameters
const createGroup = (
  name: string,
  branchMarketplaceId: string | number,
  marketplaceId: string | number
) => {
  return axios.post(baseUrl + "/rfzo_therapy_reminder_group", {
    branchMarketplaceId: branchMarketplaceId,
    marketplaceId: marketplaceId,
    name: name,
  });
};

// Updated function to accept marketplace IDs as parameters
export const fetchAllGroups = (
  branchMarketplaceId?: string | number,
  marketplaceId?: string | number
) => {
  // Guard against missing marketplace info when parameters are required
  if (!branchMarketplaceId || !marketplaceId) {
    console.warn("Cannot fetch groups: marketplace info not available");
    return Promise.reject(new Error("Missing marketplace IDs"));
  }

  return axios.get(
    baseUrl +
      `/rfzo_therapy_reminder_group/branch_marketplace_id/${branchMarketplaceId}/marketplace_id/${marketplaceId}`,
    {}
  );
};

const deleteGroup = (id: string) => {
  return axios.delete(
    baseUrl +
      "/rfzo_therapy_reminder_group/rfzo_therapy_reminder_group_id/" +
      id
  );
};

// Updated function to accept marketplace IDs as parameters
const fetchWrap = (
  setGroupList: any,
  branchMarketplaceId?: string | number,
  marketplaceId?: string | number
) => {
  if (branchMarketplaceId && marketplaceId) {
    fetchAllGroups(branchMarketplaceId, marketplaceId)
      .then((groupList: any) => {
        setGroupList(groupList.data);
      })
      .catch((error) => {
        console.error("Failed to fetch groups:", error);
      });
  }
};
